import { motion } from 'framer-motion';
import { BadgeCheck, MapPin } from 'lucide-react';
import { Stars } from './RatingSummary';
import { SOURCE_LABEL, productLabel, fmtDate } from './reviewUtils';

export const ReviewCard = ({ review: r, featured = false, index = 0 }) => {
  const body = (r.body || '').trim();
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex flex-col bg-white border border-[#E4E4E7] rounded-2xl p-5 sm:p-6 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-[3px] hover:border-[#111214]/30 hover:shadow-[0_18px_40px_-24px_rgba(17,18,20,0.35)] ${featured ? 'md:col-span-2 sm:p-8' : ''}`}
      data-testid={featured ? 'reviews-card-featured' : 'reviews-card'}
      itemScope itemType="https://schema.org/Review"
    >
      <span className={`absolute -top-3 left-6 font-display leading-none text-[#E02128] select-none ${featured ? 'text-6xl' : 'text-4xl'}`} aria-hidden="true">“</span>
      <div className="flex items-center justify-between gap-3 mb-4">
        <Stars value={r.rating} size={featured ? 18 : 14} />
        <span className="text-[11px] font-mono tracking-wider uppercase text-[#71717A] border border-[#E4E4E7] rounded-full px-2 py-0.5" data-testid="reviews-card-product">{productLabel(r)}</span>
      </div>
      <meta itemProp="itemReviewed" content={productLabel(r)} />
      <div itemProp="reviewRating" itemScope itemType="https://schema.org/Rating" className="hidden">
        <meta itemProp="ratingValue" content={String(r.rating)} /><meta itemProp="bestRating" content="5" />
      </div>
      {body ? (
        <p itemProp="reviewBody" className={`flex-1 text-[#111214] leading-relaxed whitespace-pre-line ${featured ? 'font-heading text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight flex items-center py-4' : 'text-sm sm:text-base'}`} data-testid="reviews-card-body">
          {body}
        </p>
      ) : (
        <p className="flex-1 text-[#71717A] italic text-sm">Rated {r.rating} out of 5 — no written comment.</p>
      )}
      <footer className="mt-5 pt-4 border-t border-dashed border-[#E4E4E7] flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-[#52525B]">
        <span className="font-semibold text-[#111214]" itemProp="author">{r.name}</span>
        {r.city && <span className="inline-flex items-center gap-1 text-[#71717A]"><MapPin className="w-3 h-3" />{r.city}</span>}
        {r.verified ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-mono tracking-wider uppercase text-[10px] bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="reviews-card-verified">
            <BadgeCheck className="w-3 h-3" /> Order verified
          </span>
        ) : SOURCE_LABEL[r.source] ? (
          <span className="font-mono tracking-wider uppercase text-[10px] text-[#71717A] border border-[#E4E4E7] rounded-full px-2 py-0.5">{SOURCE_LABEL[r.source]}</span>
        ) : null}
        {r.created_at && <time dateTime={r.created_at} itemProp="datePublished" className="ml-auto text-[#A1A1AA]">{fmtDate(r.created_at)}</time>}
      </footer>
    </motion.article>
  );
};
